/**
 * Board Geometry and Hole Positioning for Crazy Cribbage & Cribbage Clash.
 */

export const BOARD_CFG = Object.freeze({
  viewW: 820,
  viewH: 216,
  st1Y: 54,   // First street (0 to 30) -> Left to Right
  st2Y: 108,  // Second street (31 to 60) -> Right to Left
  st3Y: 162,  // Third street (61 to 120) -> Left to Right
  trackOffset: {
    p1: -15, p2: -10, p3: -5, p4: 0, p5: 5, p6: 10, p7: 15, p8: 20,
    you: -6, cpu: 6,
    team1: -6, team2: 6, team3: 0, team4: 6
  },
  xStart: 85,
  xEnd: 715
});

export const BOARD_CFG_481 = Object.freeze({
  viewW: 820,
  viewH: 438,
  stY: [42, 86, 130, 174, 218, 262, 306, 350],
  finishY: 394,
  xStart: 75,
  xEnd: 745
});

export function getClashBoardCfg(target = 481) {
  const t = Number(target) || 481;
  const numRows = (t === 241) ? 4 : ((t === 361) ? 6 : 8);
  const stY = [42, 86, 130, 174, 218, 262, 306, 350].slice(0, numRows);
  const finishY = stY[numRows - 1] + 44;
  const viewH = finishY + 44;
  return {
    numRows,
    viewW: 820,
    viewH,
    stY,
    finishY,
    xStart: 75,
    xEnd: 745
  };
}

export function getTrackOffset(who, playerCount = 2, isFixedTeams = false, getSeatTeamFn = null) {
  let sId = who;
  if (sId === 'you') sId = 'p1';
  if (sId === 'cpu') sId = 'p2';

  const isTeamId = (typeof sId === 'string' && sId.startsWith('team'));
  if (isFixedTeams || isTeamId) {
    const team = isTeamId
      ? sId
      : (typeof getSeatTeamFn === 'function' ? getSeatTeamFn(sId) : (sId === 'p2' || sId === 'p4' ? 'team2' : 'team1'));
    if (playerCount === 6 || playerCount === 5) {
      const offsets6 = { team1: -8, team2: 0, team3: 8 };
      return offsets6[team] !== undefined ? offsets6[team] : 0;
    } else if (playerCount === 8 || playerCount === 7) {
      const offsets8 = { team1: -9, team2: -3, team3: 3, team4: 9 };
      return offsets8[team] !== undefined ? offsets8[team] : 0;
    }
    return team === 'team1' ? -6 : 6;
  }

  if (playerCount === 2) {
    return sId === 'p1' ? -6 : 6;
  } else if (playerCount === 3) {
    const offsets3 = { p1: -8, p2: 0, p3: 8 };
    return offsets3[sId] !== undefined ? offsets3[sId] : 0;
  } else if (playerCount === 4) {
    const offsets4 = { p1: -9, p2: -3, p3: 3, p4: 9, team1: -6, team2: 6 };
    return offsets4[sId] !== undefined ? offsets4[sId] : (sId === 'team1' ? -6 : (sId === 'team2' ? 6 : 0));
  } else if (playerCount === 5) {
    const offsets5 = { p1: -12, p2: -6, p3: 0, p4: 6, p5: 12, team1: -8, team2: 0, team3: 8 };
    return offsets5[sId] !== undefined ? offsets5[sId] : 0;
  } else if (playerCount === 7) {
    const offsets7 = { p1: -15, p2: -10, p3: -5, p4: 0, p5: 5, p6: 10, p7: 15 };
    return offsets7[sId] !== undefined ? offsets7[sId] : 0;
  }
  const offsetsDefault = { p1: -9, p2: -3, p3: 3, p4: 9, team1: -6, team2: 6 };
  return offsetsDefault[sId] !== undefined ? offsetsDefault[sId] : 0;
}

export function getTrackXY(holeNum, who = 'p1', target = 121, options = {}) {
  let resolvedTarget = 121;
  let opts = options || {};

  if (typeof target === 'object' && target !== null) {
    opts = Object.assign({}, target, options);
    resolvedTarget = Number(target.target) || 121;
  } else {
    resolvedTarget = Number(target) || 0;
    if (!resolvedTarget || resolvedTarget === 121) {
      if (typeof opts === 'object' && opts !== null && opts.target) {
        resolvedTarget = Number(opts.target);
      } else if (typeof window !== 'undefined' && window.settings && window.settings.target) {
        resolvedTarget = Number(window.settings.target);
      }
    }
    if (!resolvedTarget) resolvedTarget = 121;
  }

  // If holeNum > 120, this cannot be a 121/61/31 game; infer Clash target
  if (holeNum > 120 && ![241, 361, 481].includes(resolvedTarget)) {
    if (holeNum <= 241) resolvedTarget = 241;
    else if (holeNum <= 361) resolvedTarget = 361;
    else resolvedTarget = 481;
  }

  const playerCount = Number(opts.playerCount) || 2;
  const isFixedTeams = Boolean(
    opts.isFixedTeams ||
    opts.optCanadianDoubles ||
    (opts.gameMode === 'partnership') ||
    (opts.gameMode === 'teams') ||
    (opts.gameMode === '1vs2') ||
    (typeof opts.isFixedTeams === 'function' && opts.isFixedTeams())
  );
  let offY = getTrackOffset(who, playerCount, isFixedTeams, opts.getSeatTeam);

  if (opts.optNoPassingLanes && isInNoPassingLane(holeNum)) {
    offY = 0;
  }

  const isClashTarget = [241, 361, 481].includes(resolvedTarget);

  if (isClashTarget) {
    const clashCfg = getClashBoardCfg(resolvedTarget);
    if (holeNum <= 0) {
      return { x: clashCfg.xStart - 35, y: clashCfg.stY[0] + offY };
    }
    if (holeNum >= resolvedTarget) {
      return { x: clashCfg.xStart + 32, y: clashCfg.finishY + offY };
    }
    const row = Math.floor((holeNum - 1) / 60);
    const pos = (holeNum - 1) % 60;
    if (pos < 55) {
      const frac = pos / 54;
      if (row % 2 === 0) {
        return {
          x: clashCfg.xStart + frac * (clashCfg.xEnd - clashCfg.xStart),
          y: clashCfg.stY[row] + offY
        };
      } else {
        return {
          x: clashCfg.xEnd - frac * (clashCfg.xEnd - clashCfg.xStart),
          y: clashCfg.stY[row] - offY
        };
      }
    } else {
      const k = pos - 54;
      const step = (k - 0.5) / 5;
      if (row % 2 === 0) {
        const nextY = clashCfg.stY[row + 1];
        const baseR = (nextY - clashCfg.stY[row]) / 2;
        const r = baseR - offY;
        const cy = (clashCfg.stY[row] + nextY) / 2;
        const angle = -Math.PI / 2 + step * Math.PI;
        return {
          x: clashCfg.xEnd + Math.cos(angle) * (r + 8),
          y: cy + Math.sin(angle) * r
        };
      } else {
        const nextY = (row === clashCfg.numRows - 1) ? clashCfg.finishY : clashCfg.stY[row + 1];
        const baseR = (nextY - clashCfg.stY[row]) / 2;
        const r = baseR + offY;
        const cy = (clashCfg.stY[row] + nextY) / 2;
        const angle = -Math.PI / 2 - step * Math.PI;
        return {
          x: clashCfg.xStart + Math.cos(angle) * (r + 8),
          y: cy + Math.sin(angle) * r
        };
      }
    }
  }

  const startX = BOARD_CFG.xStart;
  const endX = BOARD_CFG.xEnd;
  const t = Math.max(0, Math.min(121, holeNum));

  if (t === 0) {
    return { x: startX - 35, y: BOARD_CFG.st1Y + offY };
  }
  if (t <= 30) {
    const frac = (t - 1) / 29;
    return { x: startX + frac * (endX - startX), y: BOARD_CFG.st1Y + offY };
  }
  if (t <= 35) {
    const baseR = (BOARD_CFG.st2Y - BOARD_CFG.st1Y) / 2;
    const r = baseR - offY;
    const cy = (BOARD_CFG.st1Y + BOARD_CFG.st2Y) / 2;
    const step = (t - 30.5) / 5;
    const angle = -Math.PI / 2 + step * Math.PI;
    return {
      x: endX + Math.cos(angle) * (r + 10),
      y: cy + Math.sin(angle) * r
    };
  }
  if (t <= 65) {
    const frac = (t - 36) / 29;
    return { x: endX - frac * (endX - startX), y: BOARD_CFG.st2Y - offY };
  }
  if (t <= 70) {
    const baseR = (BOARD_CFG.st3Y - BOARD_CFG.st2Y) / 2;
    const r = baseR + offY;
    const cy = (BOARD_CFG.st2Y + BOARD_CFG.st3Y) / 2;
    const step = (t - 65.5) / 5;
    const angle = -Math.PI / 2 - step * Math.PI;
    return {
      x: startX + Math.cos(angle) * (r + 10),
      y: cy + Math.sin(angle) * r
    };
  }
  if (t <= 120) {
    const frac = (t - 71) / 49;
    return { x: startX + frac * (endX - startX), y: BOARD_CFG.st3Y + offY };
  }
  return { x: endX + 32, y: BOARD_CFG.st3Y + offY };
}

export function isInPegThroughZone(hole) {
  return (hole >= 31 && hole <= 40) || (hole >= 61 && hole <= 70) || (hole >= 91 && hole <= 100);
}

export function isInLast5PegOut(hole, target = 121) {
  return hole >= (target - 5) && hole < target;
}

export function isInBunker(hole, target = 121) {
  if (hole >= 1 && hole <= 5) return true;
  if (hole >= (target - 5) && hole < target) return true;
  if (target === 121 || [241, 361, 481].includes(target)) {
    if (hole >= 50 && hole <= 60) return true;
    if (hole >= 80 && hole <= 90) return true;
  }
  return false;
}

export function isInStinkHole(hole, target = 121) {
  return hole === (target - 1);
}

export function isInNoPassingLane(hole, lanes = []) {
  if (Array.isArray(lanes) && lanes.length > 0) {
    return lanes.some(lane => hole >= lane.start && hole <= lane.end);
  }
  return (hole >= 31 && hole <= 40) || (hole >= 61 && hole <= 70) || (hole >= 91 && hole <= 100);
}

export function streetName(score, target = 121) {
  const t = Number(target) || 121;
  if (score >= t) return 'Home';
  if ([241, 361, 481].includes(t)) {
    const numRows = t === 241 ? 4 : (t === 361 ? 6 : 8);
    const row = Math.min(numRows, Math.max(1, Math.floor((score - 1) / 60) + 1));
    const ordinals = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];
    return `${ordinals[row] || ('Street ' + row)} street`;
  }
  if (score >= 90) return 'Home';
  if (score >= 60) return 'Third street';
  if (score >= 30) return 'Second street';
  return 'First street';
}

export function skunkResult(winner, youScore, cpuScore, options = {}) {
  const target = Number(options.target || 121);
  const goal = options.goal || 'high';
  if (goal !== 'high') return null;

  const loserScore = (winner === 'you' || winner === 'p1' || winner === 'team1') ? cpuScore : youScore;
  const youLost = winner !== 'you' && winner !== 'p1' && winner !== 'team1';
  const who = youLost ? 'You were' : 'Computer was';

  if (options.optSkunkMarkers) {
    const dHole = options.doubleSkunkMarkerHole || (target === 481 ? 240 : (target === 361 ? 180 : 120));
    const sHole = options.skunkMarkerHole || (target === 481 ? 360 : (target === 361 ? 270 : 180));
    if (loserScore <= dHole) {
      return {
        kind: 'double',
        label: 'Double skunk',
        line: `${who} double-skunked (under ${dHole + 1}).`
      };
    }
    if (loserScore <= sHole) {
      return {
        kind: 'skunk',
        label: 'Skunk',
        line: `${who} skunked (under ${sHole + 1}).`
      };
    }
    return null;
  }

  if (target === 121) {
    if (loserScore < 61) {
      return {
        kind: 'double',
        label: 'Double skunk',
        line: `${who} double-skunked (under 61).`
      };
    }
    if (loserScore < 91) {
      return {
        kind: 'skunk',
        label: 'Skunk',
        line: `${who} skunked (under 91).`
      };
    }
  } else if (target === 61) {
    if (loserScore < 31) {
      return {
        kind: 'skunk',
        label: 'Skunk',
        line: `${who} skunked (under 31).`
      };
    }
  }
  return null;
}

/**
 * Computes active tracks based on settings and team mode.
 * @param {Object} [settings={}]
 * @param {boolean} [isFixedTeams=false]
 * @returns {Array<string>}
 */
export function getActiveTracks(settings = {}, isFixedTeams = false) {
  const isFixed = Boolean(
    isFixedTeams ||
    settings.optCanadianDoubles ||
    (settings.gameMode === 'partnership') ||
    (settings.gameMode === 'teams') ||
    (settings.gameMode === '1vs2') ||
    (typeof isFixedTeams === 'function' && isFixedTeams())
  );
  if (isFixed) {
    const count = Number(settings.playerCount) || 4;
    if (count === 3) return ['team1', 'team2'];
    if (count === 5 || count === 6) return ['team1', 'team2', 'team3'];
    if (count === 7 || count === 8) return ['team1', 'team2', 'team3', 'team4'];
    return ['team1', 'team2'];
  }
  const count = Number(settings.playerCount) || 2;
  const tracks = [];
  for (let i = 1; i <= count; i++) {
    tracks.push('p' + i);
  }
  return tracks;
}
